import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  MdRefresh,
  MdPersonAddAlt1,
  MdZoomIn,
  MdZoomOut,
  MdPeople,
  MdCheckCircle,
  MdInsights,
  MdSend,
  MdOpenInNew,
  MdAccountTree,
} from "react-icons/md";
import Card from "components/card";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button, { IconButton } from "components/button";
import StatCard from "components/stat-card";
import Skeleton from "components/skeleton/Skeleton";
import SideDrawer from "components/drawer";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { SegmentedControl } from "components/tabs";
import { Select } from "components/fields";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { useAuth } from "hooks/useAuth";
import teamService from "services/teamService";
import { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { getArchetype } from "lib/archetypes";
import { EMPTY, formatDate, formatNumber } from "utils/format";
import OrgChartNode, { countDescendants } from "./components/OrgChartNode";
import LeaderQuestionsPanel from "./components/LeaderQuestionsPanel";
import SubmissionCard from "./components/SubmissionCard";
import {
  ADMIN_ROLES,
  CheckInBadge,
  WeekStepper,
  fullName,
  mondayOf,
  roleLabel,
} from "./components/teamDisplay";

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 1.5;
const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 10) / 10));

const AUDIENCE_OPTIONS = [
  { value: "everyone", label: "Everyone" },
  { value: "staff", label: "Staff Only" },
];

// A node plus everyone below it; these can't become the node's manager
const collectIds = (node) => [node.id, ...(node.children || []).flatMap(collectIds)];

const responseAccent = (rate) => {
  if (rate === null) return "neutral";
  if (rate >= 75) return "sage";
  if (rate >= 40) return "gold";
  return "danger";
};

const OrgChart = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chart, setChart] = useState(null);
  const [members, setMembers] = useState([]);
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [selectedNode, setSelectedNode] = useState(null);
  const [savingManager, setSavingManager] = useState(false);
  const [sendingAssessment, setSendingAssessment] = useState(false);
  const [profile, setProfile] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNote, setSavingNote] = useState(null);
  const [staffOnly, setStaffOnly] = useState(false);
  const [focusedLeaderId, setFocusedLeaderId] = useState("");
  const [zoom, setZoom] = useState(1);
  const [draggedNode, setDraggedNode] = useState(null);
  const [overUnassign, setOverUnassign] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const chartScrollRef = useRef(null);
  const panState = useRef(null);

  // Click-and-drag panning on the chart background; cards keep their own
  // drag (reassignment) and click (drawer) behavior
  const startPan = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('[draggable="true"], [role="button"], button, select, input, a')) return;
    const el = chartScrollRef.current;
    if (!el) return;
    panState.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
    setIsPanning(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isPanning) return;
    const move = (e) => {
      const el = chartScrollRef.current;
      if (!el || !panState.current) return;
      el.scrollLeft = panState.current.left - (e.clientX - panState.current.x);
      el.scrollTop = panState.current.top - (e.clientY - panState.current.y);
    };
    const up = () => setIsPanning(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [isPanning]);

  // Pinch / ctrl+wheel zoom (non-passive listener so preventDefault works)
  useEffect(() => {
    const el = chartScrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => clampZoom(z - Math.sign(e.deltaY) * 0.1));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [loading]);

  const fetchChart = useCallback(
    async (week) => {
      try {
        setLoading(true);
        setError(null);
        const res = await teamService.getOrgChart({
          weekStart: week,
          ...(staffOnly ? { staffOnly: "true" } : {}),
        });
        setChart(res.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [staffOnly]
  );

  useEffect(() => {
    fetchChart(weekStart);
  }, [weekStart, fetchChart]);

  useEffect(() => {
    teamService
      .getMembers()
      .then((res) => setMembers(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setProfile(null);
    setNoteDrafts({});
    if (!selectedNode) return;
    teamService
      .getMemberProfile(selectedNode.id)
      .then((res) => setProfile(res.data))
      .catch(() => setProfile({ history: [] }));
  }, [selectedNode]);

  const handleAddNote = async (submissionId) => {
    const text = (noteDrafts[submissionId] || "").trim();
    if (!text) return;
    try {
      setSavingNote(submissionId);
      const res = await teamService.addNote(submissionId, text);
      setProfile((p) => ({
        ...p,
        history: (p?.history || []).map((s) => (s.id === submissionId ? res.data : s)),
      }));
      setNoteDrafts((d) => ({ ...d, [submissionId]: "" }));
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not add note"));
    } finally {
      setSavingNote(null);
    }
  };

  const handleSetManager = async (userId, managerId) => {
    try {
      setSavingManager(true);
      await teamService.setManager(userId, managerId ? Number(managerId) : null);
      notify.success("Org chart saved");
      setSelectedNode(null);
      fetchChart(weekStart);
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not update manager"));
    } finally {
      setSavingManager(false);
    }
  };

  const handleSendAssessment = async (node) => {
    const confirmed = await confirm({
      title: `Send Assessment to ${fullName(node)}?`,
      message: `${node.firstName} will get the assessment invite right away.`,
      confirmLabel: "Send",
    });
    if (!confirmed) return;
    try {
      setSendingAssessment(true);
      const res = await teamService.sendAssessment(node.id);
      const { status, reason, formName } = res.data || {};
      if (status === "sent") notify.success(`Assessment "${formName}" sent`);
      else notify.info(reason || "Nothing sent");
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not send assessment"));
    } finally {
      setSendingAssessment(false);
    }
  };

  // While dragging, the dragged person and everyone below them are invalid targets
  const invalidDropIds = useMemo(
    () => (draggedNode ? new Set(collectIds(draggedNode)) : new Set()),
    [draggedNode]
  );

  const dnd = useMemo(
    () => ({
      enabled: isAdmin,
      draggedId: draggedNode?.id || null,
      invalidIds: invalidDropIds,
      onDragStart: (node) => setDraggedNode(node),
      onDragEnd: () => {
        setDraggedNode(null);
        setOverUnassign(false);
      },
      onDrop: (target) => {
        const dragged = draggedNode;
        setDraggedNode(null);
        setOverUnassign(false);
        if (!dragged || dragged.managerId === target.id) return;
        handleSetManager(dragged.id, target.id);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, draggedNode, invalidDropIds]
  );

  // Everyone below me in the chart; leaders can manage questions for their own branch
  const mySubtreeIds = useMemo(() => {
    const findNode = (nodes, id) => {
      for (const n of nodes) {
        if (n.id === id) return n;
        const found = findNode(n.children || [], id);
        if (found) return found;
      }
      return null;
    };
    const me = user?.id ? findNode(chart?.tree || [], user.id) : null;
    return me ? new Set(collectIds(me).slice(1)) : new Set();
  }, [chart, user]);

  const canManageSelected =
    selectedNode &&
    (isAdmin || selectedNode.id === user?.id || mySubtreeIds.has(selectedNode.id));

  const responseRate = useMemo(() => {
    const s = chart?.stats;
    if (!s || !s.expectedCheckIns) return null;
    return Math.round((s.completedCheckIns / s.expectedCheckIns) * 100);
  }, [chart]);

  // People with no manager and no reports float as extra "roots"; surface them for assignment
  const unassigned = useMemo(
    () => (chart?.tree || []).filter((n) => !n.children.length && !n.managerId),
    [chart]
  );
  const rootedTrees = useMemo(
    () => (chart?.tree || []).filter((n) => n.children.length || n.managerId),
    [chart]
  );

  // Leaders who have a team; used for the "focus on a team" selector
  const leadersWithTeams = useMemo(() => {
    const out = [];
    const walk = (nodes) =>
      nodes.forEach((n) => {
        if (n.children.length) out.push(n);
        walk(n.children);
      });
    walk(chart?.tree || []);
    return out;
  }, [chart]);

  const displayedTrees = useMemo(() => {
    if (!focusedLeaderId) return rootedTrees;
    const focused = leadersWithTeams.find((n) => n.id === Number(focusedLeaderId));
    return focused ? [focused] : rootedTrees;
  }, [focusedLeaderId, leadersWithTeams, rootedTrees]);

  const stats = error ? null : chart?.stats;
  const statsLoading = loading && !chart;
  const canUnassignDragged = isAdmin && Boolean(draggedNode?.managerId);
  const archetype = getArchetype(profile?.assessment?.primaryStyle);

  let chartContent;
  if (error) {
    chartContent = (
      <ErrorState
        message={getApiErrorMessage(error, "We couldn't load the org chart.")}
        onRetry={() => fetchChart(weekStart)}
      />
    );
  } else if (loading && !chart) {
    chartContent = <Skeleton className="h-96 w-full rounded-card" />;
  } else if (!rootedTrees.length && !unassigned.length) {
    chartContent = (
      <Card padding="md">
        <EmptyState
          icon={MdAccountTree}
          title="No Team Members Yet"
          description="Staff and volunteers will appear here once they're added to your organization."
        />
      </Card>
    );
  } else {
    chartContent = (
      <Card padding="md" className="gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              aria-label="People shown"
              value={staffOnly ? "staff" : "everyone"}
              onChange={(v) => setStaffOnly(v === "staff")}
              options={AUDIENCE_OPTIONS}
            />
            <Select
              aria-label="Team"
              value={focusedLeaderId}
              onChange={(e) => setFocusedLeaderId(e.target.value)}
              className="w-56"
            >
              <option value="">All Teams</option>
              {leadersWithTeams.map((l) => (
                <option key={l.id} value={l.id}>
                  {fullName(l)}'s team
                </option>
              ))}
            </Select>
            <p className="hidden text-xs text-fg-muted xl:block">
              {isAdmin ? "Drag cards to restructure." : "Click a card for details."}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <IconButton
              icon={<MdZoomOut className="h-5 w-5" />}
              label="Zoom out"
              variant="secondary"
              size="sm"
              disabled={zoom <= ZOOM_MIN}
              onClick={() => setZoom((z) => clampZoom(z - 0.1))}
            />
            <Button
              variant="ghost"
              size="sm"
              className="min-w-[56px]"
              title="Reset zoom"
              aria-label={`Reset zoom, currently ${Math.round(zoom * 100)}%`}
              onClick={() => setZoom(1)}
            >
              {Math.round(zoom * 100)}%
            </Button>
            <IconButton
              icon={<MdZoomIn className="h-5 w-5" />}
              label="Zoom in"
              variant="secondary"
              size="sm"
              disabled={zoom >= ZOOM_MAX}
              onClick={() => setZoom((z) => clampZoom(z + 0.1))}
            />
          </div>
        </div>
        <div
          ref={chartScrollRef}
          onMouseDown={startPan}
          aria-busy={loading || undefined}
          className={`relative overflow-x-auto ${isPanning ? "cursor-grabbing select-none" : "cursor-grab"}`}
        >
          <div className="flex min-w-max items-start justify-center gap-12 pb-6" style={{ zoom }}>
            {displayedTrees.map((root) => (
              <OrgChartNode key={root.id} node={root} onSelect={setSelectedNode} dnd={dnd} />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Org Chart"
        description="Staff and volunteers in one reporting structure, with weekly check-in status at a glance."
        actions={
          <>
            <WeekStepper weekStart={weekStart} onChange={setWeekStart} />
            <Button
              variant="secondary"
              leftIcon={<MdRefresh className="h-5 w-5" />}
              loading={loading && Boolean(chart)}
              onClick={() => fetchChart(weekStart)}
            >
              Refresh
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={MdPeople}
          accent="sage"
          label="Team Members"
          value={stats ? formatNumber(stats.totalMembers) : EMPTY}
          loading={statsLoading}
        />
        <StatCard
          icon={MdCheckCircle}
          accent="sky"
          label="Checked In This Week"
          value={stats ? `${stats.completedCheckIns} / ${stats.expectedCheckIns}` : EMPTY}
          loading={statsLoading}
        />
        <StatCard
          icon={MdInsights}
          accent={responseAccent(error ? null : responseRate)}
          label="Response Rate"
          value={error || responseRate === null ? EMPTY : `${responseRate}%`}
          loading={statsLoading}
        />
        <StatCard
          icon={MdPersonAddAlt1}
          accent={unassigned.length ? "gold" : "neutral"}
          label="Needs a Team"
          value={error ? EMPTY : formatNumber(unassigned.length)}
          loading={statsLoading}
        />
      </div>

      {chartContent}

      {!error && (unassigned.length > 0 || (isAdmin && draggedNode)) && (
        <Card
          padding="md"
          className={`gap-3 transition ${
            overUnassign ? "ring-2 ring-warning" : canUnassignDragged ? "ring-1 ring-warning/60" : ""
          }`}
          onDragOver={(e) => {
            if (!canUnassignDragged) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setOverUnassign(true);
          }}
          onDragLeave={() => setOverUnassign(false)}
          onDrop={(e) => {
            if (!canUnassignDragged) return;
            e.preventDefault();
            const dragged = draggedNode;
            setDraggedNode(null);
            setOverUnassign(false);
            handleSetManager(dragged.id, null);
          }}
        >
          <div className="flex flex-wrap items-center gap-2">
            <MdPersonAddAlt1 aria-hidden="true" className="h-5 w-5 text-warning-fg" />
            <h2 className="text-heading">Not on a Team Yet ({unassigned.length})</h2>
            {canUnassignDragged && (
              <span className="text-xs font-medium text-warning-fg">
                Drop here to remove from the chart
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            {unassigned.map((node) => (
              <div
                key={node.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedNode(node)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  setSelectedNode(node);
                }}
                draggable={isAdmin}
                onDragStart={(e) => {
                  if (!isAdmin) return;
                  e.dataTransfer.effectAllowed = "move";
                  setDraggedNode(node);
                }}
                onDragEnd={() => {
                  setDraggedNode(null);
                  setOverUnassign(false);
                }}
                className={`focus-ring flex items-center gap-2 rounded-control border border-dashed border-line px-3 py-2 text-left transition-colors hover:border-accent hover:bg-surface-subtle ${
                  isAdmin ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
                } ${draggedNode?.id === node.id ? "opacity-40" : ""}`}
              >
                <Avatar user={node} name={fullName(node)} size="sm" noBorder />
                <div>
                  <p className="text-sm font-semibold text-fg">{fullName(node)}</p>
                  <p className="text-xs text-fg-muted">{node.jobTitle || roleLabel(node.role)}</p>
                </div>
              </div>
            ))}
            {unassigned.length === 0 && (
              <p className="text-sm text-fg-muted">Everyone is on a team.</p>
            )}
          </div>
        </Card>
      )}

      <SideDrawer
        isOpen={Boolean(selectedNode)}
        onClose={() => setSelectedNode(null)}
        title={selectedNode ? fullName(selectedNode) : ""}
        subtitle={selectedNode ? selectedNode.jobTitle || roleLabel(selectedNode.role) : ""}
      >
        {selectedNode && (
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-4">
              <Avatar user={selectedNode} name={fullName(selectedNode)} size="lg" noBorder />
              <div className="flex flex-col items-start gap-2">
                <Badge size="sm">{roleLabel(selectedNode.role)}</Badge>
                <Button
                  as={Link}
                  to={`/admin/team/members/${selectedNode.id}`}
                  variant="secondary"
                  size="sm"
                  rightIcon={<MdOpenInNew className="h-4 w-4" />}
                >
                  Open Full Profile
                </Button>
              </div>
            </div>

            {archetype && (
              <div className="flex items-center justify-between gap-3 rounded-control bg-surface-raised px-3 py-2">
                <div>
                  <p className="text-xs text-fg-muted">Assessment Archetype</p>
                  <p className="text-sm font-bold text-fg">{archetype.label}</p>
                </div>
                <Link
                  to={`/admin/${selectedNode.role === "volunteer" ? "volunteers" : "staff"}/people/${selectedNode.id}`}
                  className="focus-ring rounded-control text-xs font-semibold text-primary hover:underline dark:text-accent"
                >
                  View Full Assessment
                </Link>
              </div>
            )}

            {canManageSelected && (
              <Button
                variant="secondary"
                fullWidth
                leftIcon={<MdSend className="h-4 w-4" />}
                loading={sendingAssessment}
                onClick={() => handleSendAssessment(selectedNode)}
              >
                Send Assessment Now
              </Button>
            )}

            <dl className="grid grid-cols-2 gap-3">
              <div className="rounded-control bg-surface-raised p-3">
                <dt className="text-xs text-fg-muted">This Week</dt>
                <dd className="mt-1.5 text-sm">
                  {!selectedNode.managerId ? (
                    <span className="font-medium text-fg-muted">No check-in due</span>
                  ) : (
                    <span className="flex flex-col items-start gap-1">
                      <CheckInBadge submitted={selectedNode.hasSubmittedThisWeek} size="md" />
                      {selectedNode.hasSubmittedThisWeek && selectedNode.submittedAt && (
                        <span className="text-xs text-fg-muted">{formatDate(selectedNode.submittedAt)}</span>
                      )}
                    </span>
                  )}
                </dd>
              </div>
              <div className="rounded-control bg-surface-raised p-3">
                <dt className="text-xs text-fg-muted">Team Below Them</dt>
                <dd className="mt-1.5 text-sm font-bold text-fg">
                  {formatNumber(countDescendants(selectedNode))} people
                </dd>
              </div>
            </dl>

            {isAdmin && (
              <Select
                label="Reports To"
                hint="Assignments that would create a reporting loop are rejected automatically."
                disabled={savingManager}
                value={selectedNode.managerId || ""}
                onChange={(e) => handleSetManager(selectedNode.id, e.target.value || null)}
              >
                <option value="">No one (top of chart)</option>
                {members
                  .filter((m) => m.id !== selectedNode.id)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {fullName(m)} · {m.jobTitle || roleLabel(m.role)}
                    </option>
                  ))}
              </Select>
            )}

            {canManageSelected && (selectedNode.children?.length || 0) > 0 && (
              <LeaderQuestionsPanel leader={selectedNode} onChanged={() => fetchChart(weekStart)} />
            )}

            <section aria-labelledby="recent-check-ins-heading">
              <h3 id="recent-check-ins-heading" className="text-sm font-bold text-fg">
                Recent Check-Ins
              </h3>
              {profile === null ? (
                <Skeleton lines={3} className="mt-3" />
              ) : !(profile.history || []).length ? (
                <EmptyState variant="inline" title="No check-ins submitted yet." />
              ) : (
                <div className="mt-3 flex flex-col gap-3">
                  {profile.history.map((sub) => (
                    <SubmissionCard
                      key={sub.id}
                      submission={sub}
                      titleAs="h4"
                      draft={noteDrafts[sub.id]}
                      onDraftChange={(text) => setNoteDrafts((d) => ({ ...d, [sub.id]: text }))}
                      onAddNote={() => handleAddNote(sub.id)}
                      saving={savingNote === sub.id}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </SideDrawer>
    </PageContainer>
  );
};

export default OrgChart;
